import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const [comic, setComic] = useState<Comic | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleId]);

  useEffect(() => {
    const found = comics.find(c => c.id === articleId);
    if (found) {
      setComic(found);
      setNotFound(false);
    } else {
      setNotFound(true);
    }
  }, [articleId]);

  const relatedComics = comic
    ? comics.filter(c => c.id !== comic.id && c.categorySlug === comic.categorySlug).slice(0, 6)
    : [];

  const formatViews = (views: number) => {
    if (views >= 10000000) return `${(views / 10000000).toFixed(1)}千万`;
    if (views >= 10000) return `${(views / 10000).toFixed(0)}万`;
    return views.toLocaleString();
  };

  if (notFound) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center px-4">
            <div className="w-20 h-20 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-6">
              <i className="ri-file-unknow-line text-3xl text-secondary-500"></i>
            </div>
            <h1 className="text-2xl font-bold font-heading text-foreground-900 mb-3">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，你查找的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </main>
        <Footer />
      </>
    );
  }

  const mockChapters = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    number: comic.chapters - i,
    title: comic.chapters - i <= 3 ? `第${comic.chapters - i}话 - 最新章节` : `第${comic.chapters - i}话`,
    date: new Date(new Date(comic.updatedAt).getTime() - (i * 7 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0],
    isNew: i < 3,
  }));

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 免费韩漫大全`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2024-01-15',
    dateModified: comic.updatedAt,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '免费韩漫大全',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.freekcomic.com/' },
      { '@type': 'ListItem', position: 2, name: `${comic.category}韩漫`, item: `https://www.freekcomic.com/category/${comic.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: comic.title, item: `https://www.freekcomic.com/article/${comic.id}` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs w-3 h-3 flex items-center justify-center"></i>
              <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-600 transition-colors cursor-pointer">{comic.category}韩漫</Link>
              <i className="ri-arrow-right-s-line text-xs w-3 h-3 flex items-center justify-center"></i>
              <span className="text-foreground-800 font-medium">{comic.title}</span>
            </nav>
          </div>
        </div>

        <section className="py-8 md:py-12 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 md:gap-10">
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-48 sm:w-56 md:w-64 aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover"
                    title={`${comic.title} - 免费韩漫大全`}
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-primary-100 text-primary-700">
                    {comic.category}
                  </span>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                    comic.status === '连载中'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-500 border border-background-200/70">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                  {comic.title}
                </h1>

                <p className="text-sm text-foreground-600 mb-4">
                  作者：<strong>{comic.author}</strong>
                </p>

                <div className="flex flex-wrap items-center gap-6 mb-5">
                  <div className="flex items-center gap-1.5">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-foreground-900">{comic.rating}</p>
                      <p className="text-xs text-foreground-500">评分</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-eye-line text-primary-500 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-foreground-900">{formatViews(comic.views)}</p>
                      <p className="text-xs text-foreground-500">阅读量</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className="ri-book-open-line text-secondary-500 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-foreground-900">{comic.chapters}</p>
                      <p className="text-xs text-foreground-500">话</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-xl p-5 border border-background-200/70 mb-5">
                  <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => navigate(`/category/${comic.categorySlug}`)}
                    className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-circle-line mr-1.5"></i>开始阅读
                  </button>
                  <button className="px-6 py-2.5 border border-background-300 text-foreground-700 rounded-full font-medium text-sm hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-bookmark-line mr-1.5"></i>加入收藏
                  </button>
                  <button className="px-6 py-2.5 border border-background-300 text-foreground-700 rounded-full font-medium text-sm hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-share-line mr-1.5"></i>分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                章节列表
              </h2>
              <div className="flex items-center gap-3">
                <button className="text-xs font-medium text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-sort-asc mr-1"></i>正序
                </button>
              </div>
            </div>

            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
              <div className="divide-y divide-background-200/70">
                {mockChapters.map((ch) => (
                  <div
                    key={ch.number}
                    className="flex items-center justify-between px-5 py-3.5 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      {ch.isNew && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-accent-500 text-background-50 whitespace-nowrap">
                          NEW
                        </span>
                      )}
                      <span className="text-sm font-medium text-foreground-800">{ch.title}</span>
                    </div>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.date}</span>
                  </div>
                ))}
              </div>
              {comic.chapters > 20 && (
                <div className="px-5 py-4 text-center border-t border-background-200/70">
                  <p className="text-sm text-foreground-500">
                    共 {comic.chapters} 话，仅显示最近 20 话
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
                {relatedComics.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-2.5 bg-background-200">
                      <img
                        src={rel.cover}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        title={rel.title}
                        loading="lazy"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                        <p className="text-xs text-background-100/80">更新至第{rel.chapters}话</p>
                      </div>
                    </div>
                    <h3 className="text-xs font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1 mb-0.5">
                      {rel.title}
                    </h3>
                    <p className="text-xs text-foreground-500">{rel.author}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}